<template>
  <Content class="customer-detail">
    <Card>
      <div slot="title">
        <Button type="text" icon="ios-arrow-back" @click="goBack">返回</Button>
        <span style="margin-left: 10px;">用户详情</span>
      </div>

      <Spin v-if="loading" fix></Spin>

      <div v-else-if="userInfo">
        <!-- 用户基本信息 -->
        <Card title="基本信息" style="margin-bottom: 16px;">
          <Row :gutter="16">
            <Col span="4">
              <div class="user-avatar">
                <Avatar 
                  :src="userInfo.avatarUrl" 
                  size="100"
                  icon="ios-person"
                />
              </div>
            </Col>
            <Col span="10">
              <div class="info-item">
                <span class="label">用户昵称：</span>
                <span class="value">{{ userInfo.nickName || userInfo.nickname || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="label">手机号码：</span>
                <span class="value">{{ userInfo.phone || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="label">用户ID：</span>
                <span class="value">{{ userInfo._id ? userInfo._id.toString() : '-' }}</span>
              </div>
              <div class="info-item">
                <span class="label">注册时间：</span>
                <span class="value">{{ formatTime(userInfo.create_time) }}</span>
              </div>
            </Col>
            <Col span="10">
              <div class="info-item">
                <span class="label">用户状态：</span>
                <Tag :color="getUserStatusColor(userInfo.status)">
                  {{ getUserStatusText(userInfo.status) }}
                </Tag>
              </div>
              <div class="info-item">
                <span class="label">用户标签：</span>
                <Tag v-for="tag in userInfo.tags" :key="tag" color="primary">{{ tag }}</Tag>
                <span v-if="!userInfo.tags || userInfo.tags.length === 0">-</span>
              </div>
              <div class="info-item">
                <span class="label">最后登录：</span>
                <span class="value">{{ formatTime(userInfo.lastLoginTime) }}</span>
              </div>
              <div class="info-item">
                <span class="label">累计登录：</span>
                <span class="value">{{ userInfo.loginCount || 0 }} 次</span>
              </div>
              <div class="info-item merchant-row">
                <span class="label">店员权限：</span>
                <span class="value row-inline">
                  <i-switch
                    :key="(userInfo && userInfo._id) || 'u'"
                    v-model="merchantSwitch"
                    :disabled="merchantSaving"
                    @on-change="handleMerchantSwitch"
                  />
                  <span class="hint">开启后该用户小程序「我的」中显示配送工作台</span>
                </span>
              </div>
            </Col>
          </Row>
        </Card>

        <!-- 消费统计 -->
        <Card title="消费统计" style="margin-bottom: 16px;">
          <Row :gutter="16" class="stats-cards">
            <Col span="6">
              <div class="stat-card">
                <div class="stat-value">{{ stats.totalOrders || 0 }}</div>
                <div class="stat-label">总订单数</div>
              </div>
            </Col>
            <Col span="6">
              <div class="stat-card">
                <div class="stat-value">¥{{ (stats.totalSpent || 0).toFixed(2) }}</div>
                <div class="stat-label">累计消费</div>
              </div>
            </Col>
            <Col span="6">
              <div class="stat-card">
                <div class="stat-value">¥{{ (stats.avgOrderAmount || 0).toFixed(2) }}</div>
                <div class="stat-label">平均订单金额</div>
              </div>
            </Col>
            <Col span="6">
              <div class="stat-card">
                <div class="stat-value">{{ stats.completedOrders || 0 }}</div>
                <div class="stat-label">完成订单</div>
              </div>
            </Col>
          </Row>
        </Card>

        <!-- 订单历史 -->
        <Card title="订单历史" style="margin-bottom: 16px;">
          <Table :columns="orderColumns" :data="orders" border stripe>
            <template slot-scope="{ row }" slot="status">
              <Tag :color="getStatusColor(row.command)">{{ getStatusName(row.command) }}</Tag>
            </template>
            <template slot-scope="{ row }" slot="action">
              <Button type="text" size="small" @click="viewOrder(row._id)">查看</Button>
            </template>
          </Table>
          <Page 
            v-show="orderTotal > 0"
            :total="orderTotal"
            :current="orderPage"
            :page-size="10"
            show-total
            @on-change="loadOrders"
            style="margin-top: 16px; text-align: right;"
          />
        </Card>

        <!-- 操作按钮 -->
        <div class="action-buttons">
          <Button type="primary" @click="showTagModal">管理标签</Button>
        </div>
      </div>

      <div v-else>
        <Empty description="用户不存在"></Empty>
      </div>
    </Card>

    <!-- 管理标签弹窗 -->
    <Modal v-model="tagModal" title="管理用户标签" @on-ok="saveTags">
      <Form :model="tagForm">
        <FormItem label="用户标签">
          <Select v-model="tagForm.tags" multiple placeholder="选择标签">
            <Option v-for="tag in availableTags" :key="tag.value" :value="tag.value">
              {{ tag.label }}
            </Option>
          </Select>
        </FormItem>
      </Form>
    </Modal>
  </Content>
</template>

<script>
import { getCustomerDetail, getCustomerOrders, getCustomerStats, updateCustomerTags, getCustomerTags, updateCustomerMerchantFlag } from '@/api/customer'

export default {
  name: 'CustomerDetail',
  data() {
    return {
      loading: true,
      userInfo: null,
      stats: {},
      orders: [],
      orderTotal: 0,
      orderPage: 1,
      tagModal: false,
      tagForm: {
        tags: []
      },
      merchantSwitch: false,
      merchantSaving: false,
      availableTags: [
        { value: 'VIP', label: 'VIP用户' },
        { value: 'active', label: '活跃用户' },
        { value: 'new', label: '新用户' },
        { value: 'dormant', label: '沉睡用户' },
        { value: 'highValue', label: '高价值用户' }
      ],
      orderColumns: [
        { title: '订单ID', key: '_id', width: 100, render: (h, p) => h('span', p.row._id.toString().slice(-8)) },
        { title: '订单状态', slot: 'status', width: 100 },
        { title: '订单金额', key: 'total_price', width: 120, render: (h, p) => h('span', { style: { color: '#f56c6c' } }, `¥${(p.row.total_price || 0).toFixed(2)}`) },
        { title: '创建时间', key: 'create_time', width: 180, render: (h, p) => h('span', this.formatTime(p.row.create_time)) },
        { title: '操作', slot: 'action', width: 100, align: 'center' }
      ]
    }
  },
  methods: {
    async loadUserDetail() {
      const userId = this.$route.params.id
      console.log('加载用户详情，用户ID:', userId)
      if (!userId) {
        this.$Message.error('用户ID不能为空')
        return
      }

      this.loading = true
      // 清空之前的数据，避免显示错误
      this.userInfo = null
      try {
        const res = await getCustomerDetail(userId)
        console.log('用户详情API响应:', res)
        console.log('请求的用户ID:', userId)
        console.log('用户详情数据:', res.data?.data)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.userInfo = res.data.data
          console.log('当前用户信息:', this.userInfo)
          console.log('返回的用户ID:', this.userInfo?._id)
          this.tagForm.tags = this.userInfo.tags || []
          this.merchantSwitch = !!(this.userInfo && this.userInfo.is_merchant)
        } else {
          const errorMsg = res.data?.msg || res.data?.message || '获取用户详情失败'
          this.$Message.error(errorMsg)
          console.error('获取用户详情失败:', res.data)
        }
      } catch (error) {
        this.$Message.error('获取用户详情失败: ' + (error.message || error))
        console.error('获取用户详情异常:', error)
      } finally {
        this.loading = false
      }
    },
    async loadUserStats() {
      const userId = this.$route.params.id
      try {
        const res = await getCustomerStats(userId)
        console.log('用户统计API响应:', res)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.stats = res.data.data || {}
        } else {
          console.error('获取用户统计失败:', res.data)
        }
      } catch (error) {
        console.error('获取用户统计异常:', error)
      }
    },
    async loadOrders(page = 1) {
      this.orderPage = page
      const userId = this.$route.params.id
      try {
        const res = await getCustomerOrders({ userId, page, pageSize: 10 })
        console.log('用户订单API响应:', res)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.orders = res.data.data?.list || []
          this.orderTotal = res.data.data?.total || 0
        } else {
          console.error('获取订单历史失败:', res.data)
        }
      } catch (error) {
        console.error('获取订单历史异常:', error)
      }
    },
    getUserStatusText(status) {
      // 与用户列表页面的状态判断逻辑保持一致
      const statusValue = status || 'active'
      return statusValue === 'active' || statusValue === '正常' || statusValue === 1 ? '正常' : '禁用'
    },
    getUserStatusColor(status) {
      // 与用户列表页面的状态判断逻辑保持一致
      const statusValue = status || 'active'
      return (statusValue === 'active' || statusValue === '正常' || statusValue === 1) ? 'success' : 'error'
    },
    showTagModal() {
      this.tagForm.tags = this.userInfo.tags || []
      this.tagModal = true
    },
    async handleMerchantSwitch(val) {
      if (!this.userInfo || !this.userInfo._id) return
      this.merchantSaving = true
      const prev = !val
      try {
        const res = await updateCustomerMerchantFlag({
          userId: this.userInfo._id,
          is_merchant: val,
        })
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.$Message.success(val ? '已设为店员' : '已取消店员')
          this.userInfo.is_merchant = val
        } else {
          throw new Error(res.data?.msg || res.data?.message || '更新失败')
        }
      } catch (e) {
        this.merchantSwitch = prev
        this.$Message.error(typeof e === 'string' ? e : e.message || '更新失败')
      } finally {
        this.merchantSaving = false
      }
    },
    async saveTags() {
      try {
        const res = await updateCustomerTags({
          userId: this.userInfo._id,
          tags: this.tagForm.tags
        })
        
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.$Message.success('标签更新成功')
          this.userInfo.tags = this.tagForm.tags
        } else {
          this.$Message.error(res.data?.message || '更新失败')
        }
      } catch (error) {
        this.$Message.error('更新失败')
      }
    },
    viewOrder(orderId) {
      this.$router.push({
        name: 'order-detail',
        params: { id: orderId }
      })
    },
    goBack() {
      this.$router.go(-1)
    },
    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    },
    getStatusName(status) {
      const statusMap = {
        0: '待分配', 1: '已分配', 2: '前往中', 3: '已到达',
        4: '使用中', 5: '归还中', 6: '已完成', 7: '已取消', 8: '故障'
      }
      return statusMap[status] || '未知'
    },
    getStatusColor(status) {
      const colorMap = {
        0: 'default', 1: 'primary', 2: 'success', 3: 'warning',
        4: 'error', 5: 'default', 6: 'success', 7: 'error', 8: 'error'
      }
      return colorMap[status] || 'default'
    }
  },
  watch: {
    // 监听路由参数变化，当从用户A切换到用户B时重新加载数据
    '$route.params.id'(newId, oldId) {
      if (newId && newId !== oldId) {
        console.log('路由参数变化，从', oldId, '切换到', newId)
        this.loadUserDetail()
        this.loadUserStats()
        this.loadOrders()
      }
    }
  },
  mounted() {
    this.loadUserDetail()
    this.loadUserStats()
    this.loadOrders()
  }
}
</script>

<style lang="less" scoped>
.customer-detail {
  .user-avatar {
    text-align: center;
    padding: 20px;
  }
  
  .info-item {
    padding: 8px 0;
    
    .label {
      color: #909399;
      width: 80px;
      display: inline-block;
    }
    
    .value {
      color: #303133;
    }
  }

  .merchant-row {
    .row-inline {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
    .hint {
      font-size: 12px;
      color: #909399;
      max-width: 360px;
      line-height: 1.5;
    }
  }
  
  .stats-cards {
    .stat-card {
      background: #f5f7fa;
      border-radius: 8px;
      padding: 24px;
      text-align: center;
      
      .stat-value {
        font-size: 28px;
        font-weight: bold;
        color: #409EFF;
      }
      
      .stat-label {
        font-size: 14px;
        color: #909399;
        margin-top: 8px;
      }
    }
  }
  
  .action-buttons {
    margin-top: 16px;
    display: flex;
    gap: 12px;
  }
}
</style>

